import {
  ApolloClient,
  InMemoryCache,
  makeVar,
  ReactiveVar,
  Reference,
  TypePolicy,
} from '@apollo/client'
import dayjs from 'dayjs'
import debounce from 'lodash.debounce'

import {
  Encounter,
  Goal,
  PatientDependent,
  PatientMeasurement,
  Query,
  ReminderInput,
} from '@app/graphql-types/Source'
import { IcdCode } from '../constants/client_types'
import {
  ActionIconType,
  BannerState,
  EncounterAction,
  EncounterState,
} from '../constants/enums'
import { getFontSize, getIsHighContrast } from '../lib/storage'
import possibleTypes from './possibleTypes.json'
import PATIENT_MEASUREMENT_FRAGMENT from './provider/fragments/PatientMeasurement.gql'
import PROVIDER_PROFILE_QUERY from './provider/ProviderProfileQuery.gql'
import MESSAGE_FRAGMENT from './shared_fragments/Message.gql'

const streamDataItems: StreamDataItemDictionary = {}
export const showShortcut = makeVar(false)

let subscriptions: string[] = []

const debouncedSubscribe = debounce((subscriptions) => {
  const subscribe = window.subscribe
  subscribe?.(subscriptions)
}, 333)

export const ensurePatientTypePolicies = (isProvider: boolean) => {
  cache.policies.addTypePolicies({
    Patient: {
      fields: {
        isOnline: {
          read: (_existing, options) =>
            isOnlineFn('patient', options, isProvider),
        },
      },
    },
  })
}

export const isOnlineFn = (
  identifier: 'patient' | 'provider',
  options: any,
  isProvider: boolean,
) => {
  if (isProvider) {
    const modelId = options.readField('id')
    const channel = `presence:${identifier}:${modelId}`
    if (!subscriptions.includes(channel)) {
      subscriptions = subscriptions.concat(channel)
      debouncedSubscribe(subscriptions)
    }
    return Boolean(presenceVar()[channel])
  }
  return false
}

export interface DoctorsNoteData {
  endDate?: string
  message: string
  patientEmail: string
  startDate?: string
  streamId: string
}
export interface ProgressNoteData {
  auditComment?: string
  billingLabel?: string
  edit?: boolean
  encounterId?: string | null
  message: string | null
  rawIcdCodes: IcdCode[]
  reminder?: ReminderInput
  resolutionTags: string[]
  selectedReminders?: any
  type?: string | null
}

interface StreamDataItemDictionary {
  [key: string]: StreamDataItem
}

export interface RedDotProps {
  hasBeenViewedThisSession: boolean
  isBeingViewed: boolean
  isScrolledToBottom: boolean
  messageReceived: boolean
}

interface EncounterInfoPanelProps {
  isPanelOpen: boolean
}

interface EncounterPanelProps extends EncounterInfoPanelProps {
  action: EncounterAction
}

interface PanelScrollPositions {
  [key: string]: { scrollTop: number; selector: string }
}

interface SidePanelProps extends EncounterInfoPanelProps {
  selectedActionIcon: ActionIconType
}

export interface StreamDataItem {
  editProgressNote: ReactiveVar<Partial<ProgressNoteData> | null>
  encounterInfoPanel: ReactiveVar<boolean>
  encounterPanel: ReactiveVar<EncounterPanelProps>
  key: string
  progressNote: ReactiveVar<Partial<ProgressNoteData> | null>
  providerNoteInputValue: ReactiveVar<string>
  redDot: ReactiveVar<RedDotProps>
  sidebarMessagePreview: ReactiveVar<string | null>
  sidePanel: ReactiveVar<SidePanelProps>
  sidePanelScrollPositions: ReactiveVar<PanelScrollPositions>
  typingUserName: ReactiveVar<string | null>
}

const initialStreamData = (streamId: string) => ({
  editProgressNote: makeVar(null),
  encounterInfoPanel: makeVar(false),
  encounterPanel: makeVar({
    action: EncounterAction.Default,
    isPanelOpen: false,
  }),
  key: streamId,
  progressNote: makeVar(null),
  providerNoteInputValue: makeVar(''),
  redDot: makeVar({
    hasBeenViewedThisSession: false,
    isBeingViewed: false,
    isScrolledToBottom: false,
    messageReceived: false,
  }),
  sidebarMessagePreview: makeVar(null),
  sidePanel: makeVar({
    isPanelOpen: false,
    selectedActionIcon: ActionIconType.DEFAULT,
  }),
  sidePanelScrollPositions: makeVar({}),
  typingUserName: makeVar(null),
})

export const getStreamDataItem = (streamId: string): StreamDataItem => {
  if (!streamDataItems[streamId]) {
    streamDataItems[streamId] = initialStreamData(streamId)
  }
  return streamDataItems[streamId]
}
export interface PatientStreams {
  [key: string]: {
    [key: string]: boolean
  }
}

const streamDataItemFields: TypePolicy = {
  fields: {
    streamDataItem: {
      read: (_existing, options) => {
        // when coming in from an `Encounter` this should use `streamId`,
        // when coming in from a `Stream` the `id` is what we want
        const streamId: string | undefined =
          options.readField('streamId') ?? options.readField('id')
        if (!streamId) {
          return null
        }
        return getStreamDataItem(streamId)
      },
    },
  },
}

const pagingFields = (isNetworkOnly: boolean = false): TypePolicy => ({
  fields: {
    data: {
      keyArgs: false,
      merge: (existing = [], incoming: any[], { variables }) => {
        // always return incoming if it is network-only and the first page.
        // this ensures stateful queries are reset back to zero state for
        // keeping server data the source of truth when cached state can change
        // due to a web socket event (encounter state or provider shift state)
        if (
          isNetworkOnly &&
          (!variables?.page || Number(variables?.page) === 1)
        ) {
          return incoming
        }

        // remove existing from incoming and then merge
        const filteredIncoming = incoming.filter((item) => {
          return existing.every((oldItem: any) => {
            if (oldItem.profile !== undefined) {
              return oldItem.profile.__ref !== item.profile.__ref
            }
            return oldItem.__ref !== item.__ref
          })
        })
        return [...existing, ...filteredIncoming]
      },
    },
  },
})

// encounter:updated events can trigger the `ProviderProfileQuery` to load new
// active encounters. if the stream had ben visited while `activeEncounter` was
// `null` we will never get the reference to the newly assigned encounter, so
// here we add the encounter ref to the existing stream object. `modify` only
// runs the field policies if the cache id exists.
const providerProfileFields: TypePolicy = {
  fields: {
    activeEncounters: {
      keyArgs: false,
      merge: (_existing = [], incoming: any[], { readField }) => {
        incoming.forEach((encounterRef) => {
          const streamId = readField('streamId', encounterRef)
          cache.modify({
            id: `Stream:${streamId}`,
            fields: {
              activeEncounter: () => encounterRef,
            },
          })
        })
        return incoming
      },
    },
  },
}

// Note: specifying keyArgs will change the shape of the cache key in Apollo. e.g. it may be in object form like patientGoals:{"patientId":"999","status":"in_progress"}
const queryFields: TypePolicy = {
  fields: {
    patientGoals: {
      keyArgs: ['patientId', 'status'],
    },
    patientProfile: {
      merge: (existing, incoming, { mergeObjects }) => {
        return mergeObjects(existing, incoming)
      },
    },
    providerProfile: {
      merge: (existing, incoming, { mergeObjects }) => {
        return mergeObjects(existing, incoming)
      },
    },
    stream: {
      merge: (existing, incoming, { mergeObjects }) => {
        return mergeObjects(existing, incoming)
      },
    },
  },
}

// This ensures that the `id` property is always a string when coming from web
// socket events and writing data directly to the cache
const coerceIdToString: TypePolicy = {
  fields: {
    id: {
      merge: (_, incoming) => `${incoming}`,
    },
  },
}

export const createCache = () => {
  return new InMemoryCache({
    possibleTypes: {
      ...possibleTypes,
      CoerceIdToString: ['Patient', 'Plan', 'Provider', 'Shift'],
    },
    typePolicies: {
      CoerceIdToString: coerceIdToString,
      // `ProviderEncounter` fragment streamDataItem @client directive
      Encounter: streamDataItemFields,
      EncounterMessages: pagingFields(),
      EncounterSearch: pagingFields(true),
      Goals: pagingFields(true),
      Messages: pagingFields(),

      /****
        // The Patient type policy is added to the cache within a ConfigurationContext
        ****/

      PatientMeasurements: pagingFields(true),
      ProviderProfile: providerProfileFields,
      ProviderProgressNotes: pagingFields(),
      ProviderSearch: pagingFields(true),
      Query: queryFields,
      Reminders: pagingFields(true),
      LabOrders: pagingFields(true),
      StandardPhrasesQuery: pagingFields(),
      // `PatientStream` fragment streamDataItem @client directive
      Stream: streamDataItemFields,
      StreamSearch: pagingFields(),
    },
  })
}

export const cache: InMemoryCache = createCache()

export const getExistingItem = (existing, newItem) => {
  return existing.data.find(
    (item: any) =>
      item.id === newItem.id ||
      item.__ref?.includes(newItem.id) ||
      item.__ref === newItem.__ref,
  )
}
export const getRefId = (ref: Reference) => Number(ref.__ref.split(':').pop())

export const insertMessage = (client: ApolloClient<object>, message) => {
  const { cache } = client
  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      [`messages({"streamId":"${message.streamId}"})`]: (
        existing: any = [],
      ) => {
        // we need to guard against websocket message:added firing and
        // loading the message before message creation returns the result
        if (getExistingItem(existing, message) && !message.edited) {
          return existing
        }

        // we do not want to dup the message after it has been edited
        let existingData = existing.data
        if (message.edited) {
          existingData = existing.data.filter((msg) => msg.id !== message.id)
        }

        const newReference = cache.writeFragment({
          data: message,
          fragment: MESSAGE_FRAGMENT,
          fragmentName: 'Message',
        })

        const newData = [newReference, ...existingData].sort((a, b) => {
          return getRefId(b) - getRefId(a)
        })
        if (getRefId(newData[newData.length - 1]) < 0) {
          newData.unshift(newData.pop())
        }
        return {
          ...existing,
          data: newData,
        }
      },
    },
  })
  requestAnimationFrame(() =>
    getStreamDataItem(message.streamId).typingUserName(null),
  )
}

export const updateActiveEncounters = (
  client: ApolloClient<object>,
  encounter: Encounter,
) => {
  const { cache } = client
  const profileData: Query | null = cache.readQuery({
    query: PROVIDER_PROFILE_QUERY,
  })
  const { activeEncounters, profile } = profileData?.providerProfile || {}
  const isMyEncounter = `${encounter?.provider?.id}` === `${profile?.id}`
  const shouldAdd =
    isMyEncounter &&
    [
      EncounterState.ACTIVE,
      EncounterState.ASSIGNED,
      EncounterState.HOT,
    ].includes(encounter.state as EncounterState)
  const activeEncounterIndex =
    activeEncounters?.findIndex((e) => `${e.id}` === `${encounter.id}`) ?? -1
  const listHasEncounter = activeEncounterIndex >= 0
  if (shouldAdd && !listHasEncounter) {
    // add to active encounters
    const newEncounters = [...(activeEncounters || []), encounter].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    )
    cache.writeQuery({
      data: {
        providerProfile: {
          ...profileData,
          activeEncounters: newEncounters,
          profile,
        },
      },
      query: PROVIDER_PROFILE_QUERY,
    })
  } else if (!shouldAdd && listHasEncounter) {
    // remove from active encounters
    const newEncounters = activeEncounters?.filter(
      (_e, i) => i !== activeEncounterIndex,
    )
    cache.writeQuery({
      data: {
        providerProfile: {
          ...profileData,
          activeEncounters: newEncounters,
          profile,
        },
      },
      query: PROVIDER_PROFILE_QUERY,
    })
  }
}

export const insertPatientMeasurement = (
  client: ApolloClient<object>,
  patientId: string,
  measurement: PatientMeasurement,
) => {
  const fieldArgs = `{"measurementType":"${measurement.measurementType}","patientId":"${patientId}"}`
  const fieldName = `patientMeasurements(${fieldArgs})`
  const { cache } = client

  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      [fieldName]: (existing: any) => {
        const newReference = cache.writeFragment({
          data: measurement,
          fragment: PATIENT_MEASUREMENT_FRAGMENT,
          fragmentName: 'PatientMeasurement',
        })
        return {
          ...existing,
          data: [newReference, ...existing.data],
        }
      },
    },
  })
}

export const insertGoal = (
  client: ApolloClient<object>,
  goal: Goal,
  patientId: string,
) => {
  let fieldArgs = `"patientId":"${patientId}","status":"${goal.status}"`
  const fieldName = `patientGoals:{${fieldArgs}}`
  const { cache } = client

  const goalRef = cache.identify(goal)
  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      [fieldName]: (existing: any) => {
        return {
          ...existing,
          data: [...existing.data, { __ref: goalRef }],
        }
      },
    },
  })
}

export const currentDependentVar: ReactiveVar<PatientDependent | null> =
  makeVar(null)
export const fontSizeVar: ReactiveVar<number> = makeVar(getFontSize())
export const isHighContrastVar: ReactiveVar<boolean> =
  makeVar(getIsHighContrast())
export const presenceVar: ReactiveVar<any> = makeVar({})
export const redDotUpdatedVar: ReactiveVar<number> = makeVar(0)
export const redDotUpdated = () => {
  redDotUpdatedVar(redDotUpdatedVar() + 1)
}

export interface NotificationPayload {
  state: BannerState
  text: string
  timeout?: null | number
}

interface NotificationState extends NotificationPayload {
  time: number
}

export const notificationBannerState = makeVar<NotificationState>({
  state: BannerState.DEFAULT,
  text: '',
  time: 0,
})

export const lastActivityVar = makeVar<number>(dayjs().unix())
export const lastKeepAliveVar = makeVar<number>(dayjs().unix())

export const setNotificationBanner = (payload: NotificationPayload) => {
  notificationBannerState({ ...payload, time: dayjs().unix() })
}

export const updateUnsignedLabResultsCount = (
  client: ApolloClient<object>,
  providerId: string,
  count: number,
) => {
  const { cache } = client

  cache.modify({
    id: `Provider:${providerId}`,
    fields: {
      unsignedLabResultsCount: () => count,
    },
  })
}
